<template>
  <div
    :class="getClassName"
    @change="handleItemChange($event.target.value)"
    @click="handleItemClick()"
  >
    <InputValidator
      :config="{
        dataLpignore: true,
        errorStyleExceptions: getErrorExceptionStyle,
        name: field,
        placeholder: placeholder,
        readonly: !isSelected || showReadOnly,
        validate: validation,
        value: item[field],
      }"
      :name="field"
      @InputValidatorChange="handleValueChange"
      @InputValidatorFocus="handleItemFocus"
      @InputValidatorModel="handleValueInput"
    />
  </div>
</template>
<script>
import { extend } from 'lodash';
import { mapActions, mapGetters } from 'vuex';
import InputValidator from 'client/components/FormValidation/InputValidator.vue';

export default {
  name: 'EditableInput',

  components: {
    InputValidator,
  },

  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    field: {
      type: String,
      default: '',
    },
    hideErrorValidation: {
      type: Boolean,
      default: false,
    },
    item: {
      required: true,
      type: Object,
    },
    placeholder: {
      type: String,
      default: '',
    },
    showNarrowInput: {
      type: Boolean,
      default: false,
    },
    showReadOnly: {
      type: Boolean,
      default: false,
    },
    storeModule: {
      type: String,
      default: 'ImportUsersCollection',
    },
    updateItemErrorHandler: {
      default: () => {},
      type: Function,
    },
    validateDuplicates: {
      type: Boolean,
      default: false,
    },
    validation: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      errorNotified: false,
    };
  },

  computed: {
    getClassName() {
      switch (this.showNarrowInput) {
        case true:
          return this.$style.textInputNarrow;
        default:
          return this.$style.textInput;
      }
    },
    getErrorExceptionStyle() {
      switch (this.hideErrorValidation) {
        case true:
          return this.$style.hidden;
        default:
          return this.$style.error;
      }
    },
    isSelected() {
      return this.selectedItems.indexOf(this.item.id) !== -1;
    },
  },

  watch: {
    isSelected(newValue) {
      const errorObject = { selectedItemId: this.item.id, fieldName: this.field };
      if (newValue === true) {
        this.$validator.validateAll().then((valid) => {
          if (!valid) {
            this.addValidationError(errorObject);
          }
        });
      } else if (this.$children[0].$validator.errors.items.length > 0) {
        this.removeValidationError(errorObject);
      }
      this.$children[0].$validator.errors.clear();
    },
  },

  beforeCreate() {
    const storeModule = this.$options.propsData.storeModule
      || this.$options.props.storeModule.default;

    extend(
      this.$options.computed,
      mapGetters(storeModule, {
        selectedItems: 'getSelectedItems',
        validationErrors: 'getValidationErrors',
      }),
    );

    extend(
      this.$options.methods,
      mapActions(storeModule, [
        'addDuplicateValidationError',
        'addValidationError',
        'removeDuplicateValidationError',
        'removeValidationError',
        'selectItem',
        'updateItem',
      ]),
    );
  },

  methods: {
    handleItemChange(value) {
      const isValid = this.validationErrors.length > 0
      && this.validationErrors.some(i => i.selectedItemId === this.item.id);
      if (!isValid && !this.errorNotified) {
        this.updateItemWrapper({ id: this.item.id, key: this.field, value });
      }
    },

    handleItemClick() {
      if (this.disabled) {
        return;
      }

      this.selectItem({ id: this.item.id });
    },

    handleItemFocus() {
      if (this.disabled) {
        return;
      }

      this.selectItem({ id: this.item.id });
    },

    async handleValueChange(value) {
      const valid = await this.$validator.validate(this.field);
      const errorObject = { selectedItemId: this.item.id, fieldName: this.field };
      if (!valid) {
        this.addValidationError(errorObject);
      } else {
        this.updateItemWrapper({ id: this.item.id, key: this.field, value });
        this.removeValidationError(errorObject);
      }
    },

    async handleValueInput() {
      const valid = await this.$validator.validate(this.field);
      const errorObject = { selectedItemId: this.item.id, fieldName: this.field };
      if (!valid) {
        this.addValidationError(errorObject);
      } else {
        this.removeValidationError(errorObject);
      }
    },

    async updateItemWrapper(payload) {
      const error = await this.updateItem(payload).catch(e => e);
      const { id, key } = payload;
      const errorIndex = { selectedItemId: id, fieldName: key };
      if (error) {
        if (error.message === 'Already Exists') {
          if (this.validateDuplicates) this.addDuplicateValidationError(errorIndex);
          this.updateItemErrorHandler(error, payload);
          this.errorNotified = true;
        } else {
          this.updateItemErrorHandler(error, payload);
          this.errorNotified = true;
        }
      } else {
        if (this.validateDuplicates) {
          this.removeDuplicateValidationError({ selectedItemId: id, fieldName: key });
        }
        this.errorNotified = false;
      }
    },
  },
};
</script>
<style module>
@value (jcError) from '~css/brand-colors.css';

.error {
  color: jcError;
  font-size: 0.85em;
  font-weight: 400;
  line-height: 1.33em;
  min-height: 1.33em;
  position: relative;
}

.hidden { display: none; }

.textInput {
  composes: textInput from '~client/directories/Workday/Workday.css';
  margin-bottom: 5px;
  padding: 0;
}

.textInput input[readonly] {
  padding-left: 0;
}

.textInputNarrow {
  composes: textInput;
  width: 60%;
}
</style>
